import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  AlertTriangle, Plus, Search, Filter, TrendingUp, TrendingDown, 
  Minus, MoreVertical, Edit, Trash2, Eye, Shield, Target
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  getRiskRegister, createRisk, updateRisk, deleteRisk,
  RISK_CATEGORIES, RISK_STATUSES, getRiskLevelFromScore 
} from '@/services/risk';
import { toast } from 'sonner';

const RiskRegisterPage = () => {
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [total, setTotal] = useState(0);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingRisk, setEditingRisk] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'operational',
    source: '',
    inherent_likelihood: 3,
    inherent_impact: 3,
    owner: '',
    department: '',
    tags: []
  });

  const loadRisks = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (categoryFilter !== 'all') params.category = categoryFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const data = await getRiskRegister(params);
      setRisks(data.risks || []);
      setTotal(data.total || 0);
    } catch (error) {
      toast.error('Failed to load risks');
    } finally {
      setLoading(false);
    }
  }, [search, categoryFilter, statusFilter]);

  useEffect(() => {
    loadRisks();
  }, [loadRisks]);

  const handleSubmit = async () => {
    try {
      if (editingRisk) {
        await updateRisk(editingRisk.id, formData);
        toast.success('Risk updated successfully');
      } else {
        await createRisk(formData);
        toast.success('Risk created successfully');
      }
      setShowCreateDialog(false);
      setEditingRisk(null);
      resetForm();
      loadRisks();
    } catch (error) {
      toast.error('Failed to save risk');
    }
  };

  const handleDelete = async (risk) => {
    if (window.confirm(`Delete risk "${risk.title}"?`)) {
      try {
        await deleteRisk(risk.id);
        toast.success('Risk deleted');
        loadRisks();
      } catch (error) {
        toast.error('Failed to delete risk');
      }
    }
  };

  const openEditDialog = (risk) => {
    setEditingRisk(risk);
    setFormData({
      title: risk.title,
      description: risk.description,
      category: risk.category,
      source: risk.source || '',
      inherent_likelihood: risk.inherent_likelihood,
      inherent_impact: risk.inherent_impact,
      owner: risk.owner,
      department: risk.department || '',
      tags: risk.tags || []
    });
    setShowCreateDialog(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'operational',
      source: '',
      inherent_likelihood: 3,
      inherent_impact: 3,
      owner: '',
      department: '',
      tags: []
    });
  };

  const getRiskLevelBadge = (score) => {
    const level = getRiskLevelFromScore(score);
    const colors = {
      critical: 'bg-red-100 text-red-800 border-red-200',
      high: 'bg-orange-100 text-orange-800 border-orange-200',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      low: 'bg-green-100 text-green-800 border-green-200'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${colors[level.level]}`}>
        {level.label} ({score})
      </span>
    );
  };

  const getCategoryBadge = (category) => {
    const cat = RISK_CATEGORIES.find(c => c.value === category);
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
        {cat?.label || category}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const colors = {
      identified: 'bg-gray-100 text-gray-700',
      assessed: 'bg-blue-100 text-blue-700',
      mitigating: 'bg-yellow-100 text-yellow-700',
      accepted: 'bg-green-100 text-green-700',
      transferred: 'bg-purple-100 text-purple-700',
      closed: 'bg-gray-100 text-gray-500'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100'}`}>
        {status?.replace('_', ' ')}
      </span>
    );
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Risk Register</h1>
          <p className="text-gray-500 mt-1">Manage and track organizational risks</p>
        </div>
        <Button onClick={() => { resetForm(); setShowCreateDialog(true); }} className="gap-2">
          <Plus className="w-4 h-4" />
          Add Risk
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Risks</p>
                <p className="text-2xl font-bold">{total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <Target className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Critical/High</p>
                <p className="text-2xl font-bold">
                  {risks.filter(r => r.inherent_risk_score >= 12).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Shield className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Mitigating</p>
                <p className="text-2xl font-bold">
                  {risks.filter(r => r.status === 'mitigating').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <TrendingDown className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Closed</p>
                <p className="text-2xl font-bold">
                  {risks.filter(r => r.status === 'closed').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search risks..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {RISK_CATEGORIES.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {RISK_STATUSES.map(stat => (
                  <SelectItem key={stat.value} value={stat.value}>{stat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Risk Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Risk ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Inherent Risk</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Residual Risk</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Owner</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {loading ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : risks.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                      No risks found. Click "Add Risk" to create one.
                    </td>
                  </tr>
                ) : (
                  risks.map(risk => (
                    <tr key={risk.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="px-4 py-3 text-sm font-mono text-gray-600">{risk.risk_id}</td>
                      <td className="px-4 py-3">
                        <Link to={`/grc/risk/register/${risk.id}`} className="font-medium text-blue-600 hover:underline">
                          {risk.title}
                        </Link>
                      </td>
                      <td className="px-4 py-3">{getCategoryBadge(risk.category)}</td>
                      <td className="px-4 py-3">{getRiskLevelBadge(risk.inherent_risk_score)}</td>
                      <td className="px-4 py-3">{getRiskLevelBadge(risk.residual_risk_score)}</td>
                      <td className="px-4 py-3">{getStatusBadge(risk.status)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{risk.owner}</td>
                      <td className="px-4 py-3 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditDialog(risk)}>
                              <Edit className="w-4 h-4 mr-2" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleDelete(risk)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingRisk ? 'Edit Risk' : 'Add New Risk'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="text-sm font-medium">Title *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Risk title"
                />
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium">Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Detailed description of the risk"
                  className="w-full p-2 border rounded-md min-h-[100px]"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Category *</label>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RISK_CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Source</label>
                <Input
                  value={formData.source}
                  onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                  placeholder="Where was this risk identified?"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Likelihood (1-5)</label>
                <Select 
                  value={String(formData.inherent_likelihood)} 
                  onValueChange={(v) => setFormData({ ...formData, inherent_likelihood: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1,2,3,4,5].map(n => (
                      <SelectItem key={n} value={String(n)}>{n} - {['Rare','Unlikely','Possible','Likely','Almost Certain'][n-1]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Impact (1-5)</label>
                <Select 
                  value={String(formData.inherent_impact)} 
                  onValueChange={(v) => setFormData({ ...formData, inherent_impact: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1,2,3,4,5].map(n => (
                      <SelectItem key={n} value={String(n)}>{n} - {['Negligible','Minor','Moderate','Major','Catastrophic'][n-1]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Owner *</label>
                <Input
                  value={formData.owner}
                  onChange={(e) => setFormData({ ...formData, owner: e.target.value })}
                  placeholder="Risk owner name"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Department</label>
                <Input
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  placeholder="Department"
                />
              </div>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                <strong>Risk Score:</strong> {formData.inherent_likelihood * formData.inherent_impact} 
                ({getRiskLevelFromScore(formData.inherent_likelihood * formData.inherent_impact).label})
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={!formData.title || !formData.description || !formData.owner}>
              {editingRisk ? 'Update' : 'Create'} Risk
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RiskRegisterPage;
